import _ from 'lodash';
import  { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { quickAdjustmentPropTypes } from 'common/propTypes';
import { QUICK_ADJUSTMENT_TRIGGERS, QUICK_ADJUSTMENT_ACTIONS } from 'v2/common/constants/index';
import useOutputSchema from 'v2/modules/withRunForm/hooks/useOutputSchema';
import { OutputSchemaUtility } from 'v2/common/quantConfig/outputSchema/utility';
import { extractIndividualSchemaField } from 'v2/modules/withRunForm/utils/signal';
import { IntentsContext } from 'v2/modules/withRunForm/hoc/withIntentsContext';
import { isCaseBasedAction } from './Utils';
import  QuickAdjustmentTypeahead  from './QuickAdjustmentTypeahead';

export const BLANK_QUICK_ADJUSTMENT = {
  trigger: QUICK_ADJUSTMENT_TRIGGERS.ExitedInLoss.value,
  action: QUICK_ADJUSTMENT_ACTIONS.CloseLeg.value,
  targets: [],
};

const propTypes = {
  intentId: PropTypes.string.isRequired,
  quickAdjustments: PropTypes.arrayOf(quickAdjustmentPropTypes),
  onUpdate: PropTypes.func.isRequired,
};

const defaultProps = {
  quickAdjustments: [BLANK_QUICK_ADJUSTMENT],
};

const QuickAdjustmentsForm = ({ intentId, quickAdjustments, onUpdate }) => {
  const { modifyCasesTargetSchema } = useContext(IntentsContext);
  const outputSchema = useOutputSchema();
  const legOptions = useMemo(() => {
    return _.chain(outputSchema)
      .thru((schema) => OutputSchemaUtility.filterByCategory(schema, 'individualStop'))
      .thru((schema) => _.map(schema, (sch) => extractIndividualSchemaField(sch?.name)?.intentId))
      .uniq()
      .without(intentId)
      .map((id) => ({ id: id.toString(), label: `Leg ${id}` }))
      .value();
  }, [intentId, outputSchema]);

  const addQuickAdjustment = useCallback(() => {
    onUpdate([...quickAdjustments, BLANK_QUICK_ADJUSTMENT]);
  }, [quickAdjustments, onUpdate]);

  const onDeleteQuickAdjustment = useCallback(
    (index) => {
      onUpdate(_.filter(quickAdjustments, (_, i) => i !== index));
    },
    [quickAdjustments, onUpdate]
  );

  const updateQuickAdjustment = useCallback(
    (index, updatedQuickAdjustment) => {
      const newQuickAdjustments = [...quickAdjustments];
      newQuickAdjustments[index] = updatedQuickAdjustment;
      onUpdate(newQuickAdjustments);
    },
    [quickAdjustments, onUpdate]
  );

  return (
    <div className="quick-adjustments-container rbt-main-form">
      {quickAdjustments.map((quickAdjustment, index) => {
        const TypeaheadOptions = isCaseBasedAction(quickAdjustment.action) ? modifyCasesTargetSchema : legOptions;
        return (
          <QuickAdjustmentTypeahead
            key={index}
            quickAdjustment={quickAdjustment}
            TypeaheadOptions={TypeaheadOptions}
            index={index}
            onUpdate={updateQuickAdjustment}
            onDelete={onDeleteQuickAdjustment}
            intentId={intentId}
          />
        )
      })}
      <div className="d-flex align-items-center justify-content-center">
        <button
          type="button"
          className="add-quick-adjustment btn btn-secondary py-1 px-2 d-flex align-items-center"
          onClick={addQuickAdjustment}
        >
          <i className="fa-regular fa-plus-large me-1 text-color" />
          Add Quick Action
        </button>
      </div>
    </div>
  );
};

QuickAdjustmentsForm.propTypes = propTypes;
QuickAdjustmentsForm.defaultProps = defaultProps;

export default QuickAdjustmentsForm;
